import React, { useState } from 'react';
import { Clock, Calendar, Globe, ChevronDown, AlertTriangle } from 'lucide-react';

/**
 * DateMocker component allows setting a fake date/time for testing purposes
 * @param {function} onApplyFakeDate - Callback function when fake date is applied
 * @param {boolean} bypassCache - Whether to bypass cache when fetching data
 */
const DateMocker = ({ onApplyFakeDate, bypassCache = true }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const handleApplyFakeDate = () => {
    let date = document.getElementById('fake_date').value;
    let time = document.getElementById('fake_time').value;
    let tz = document.getElementById('fake_tz').value;
    let fake_date = `${date} ${time} ${tz}`;

    onApplyFakeDate(bypassCache, fake_date);
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md dark:shadow-gray-900/20 overflow-hidden border border-gray-100 dark:border-gray-700 hover:shadow-lg dark:hover:shadow-gray-900/20 transition-shadow duration-300">
      <div 
        className="px-5 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-3">
          <div className="bg-primary-50 p-2 rounded-lg">
            <Clock className="w-5 h-5 text-primary-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Date Simulator</h3>
            <p className="text-xs text-gray-500">Test with different date and time settings</p>
          </div>
        </div>
        <ChevronDown 
          className={`w-5 h-5 text-gray-400 transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`} 
        />
      </div>
      
      {isExpanded && (
        <div className="p-5 bg-gray-50 dark:bg-gray-900">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="flex flex-col">
              <label htmlFor="fake_date" className="text-xs font-medium text-gray-600 mb-1.5 flex items-center gap-1.5">
                <Calendar className="w-3.5 h-3.5" />
                Date
              </label>
              <div className="relative">
                <input 
                  id="fake_date" 
                  type="date" 
                  className="w-full px-4 py-2.5 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm dark:shadow-gray-900/20 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors duration-200" 
                  defaultValue={new Date().toISOString().substring(0, 10)}
                />
              </div>
            </div>
            
            <div className="flex flex-col">
              <label htmlFor="fake_time" className="text-xs font-medium text-gray-600 mb-1.5 flex items-center gap-1.5">
                <Clock className="w-3.5 h-3.5" />
                Time
              </label>
              <input 
                id="fake_time" 
                type="time" 
                className="w-full px-4 py-2.5 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm dark:shadow-gray-900/20 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors duration-200" 
                defaultValue={new Date().toISOString().substring(11, 16)}
              />
            </div>
            
            <div className="flex flex-col">
              <label htmlFor="fake_tz" className="text-xs font-medium text-gray-600 mb-1.5 flex items-center gap-1.5">
                <Globe className="w-3.5 h-3.5" />
                Timezone
              </label>
              <div className="relative">
                <select 
                  id="fake_tz" 
                  className="w-full appearance-none px-4 py-2.5 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm dark:shadow-gray-900/20 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors duration-200" 
                  defaultValue={Intl.DateTimeFormat().resolvedOptions().timeZone}
                >
                  {Intl.supportedValuesOf('timeZone').map((tz, index) => (
                    <option key={index}>{tz}</option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
              </div>
            </div>
            
            <div className="flex flex-col justify-end">
              <button 
                className="w-full px-4 py-2.5 rounded-lg bg-primary-600 text-white shadow-sm dark:shadow-gray-900/20 hover:bg-primary-700 transition-all duration-200 focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 font-medium"
                onClick={handleApplyFakeDate}
              >
                Apply Date
              </button>
            </div>
          </div>
          
          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            <div className="flex items-start gap-2 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-amber-800 mb-1">Test participants only</p>
                <div className="text-xs text-amber-700 space-y-2">
                  <p>
                    Applying a simulated date writes window statuses to the database as if the simulated time were now. Survey availability, due dates, and conditional flows will reflect the simulated time until the next scheduled recalculation re-evaluates them against real time.
                  </p>
                  <p>
                    Do not apply this to real study participants — use a test participant under your sponsor.
                  </p>
                </div>
              </div>
            </div>
            <div className="text-xs text-gray-500 mt-3 space-y-1">
              <p className="font-medium text-gray-600">How to use</p>
              <ol className="list-decimal pl-5 space-y-0.5">
                <li>Pick a date, time, and timezone, then click <span className="font-medium">Apply Date</span>.</li>
                <li>The page reloads with surveys, statuses, and due dates as they would appear at that moment.</li>
                <li>To return to normal, refresh without applying — scheduled processing will reset window statuses on its next run.</li>
              </ol>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DateMocker;
